/**
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import complaintService from '../../services/complaintService';
import LoadingSpinner from '../common/LoadingSpinner';

const Badge = ({ text, type }) => {
  const classes = {
    pending: 'bg-yellow-100 text-yellow-800',
    in_progress: 'bg-blue-100 text-blue-800',
    resolved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    low: 'bg-gray-100 text-gray-800',
    medium: 'bg-orange-100 text-orange-800',
    high: 'bg-red-100 text-red-800',
  }[type] || 'bg-gray-100 text-gray-800';

  return (
    <span className={`px-2 py-0.5 text-xs rounded-full ${classes}`}>
      {text}
    </span>
  );
};

const ComplaintList = () => {
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const data = await complaintService.getComplaints();
      setComplaints(data.complaints || []);
    } catch (err) {
      toast.error('Failed to load complaints');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="max-w-6xl mx-auto mt-6 px-4">
      <h2 className="text-xl font-semibold mb-4">Your Complaints</h2>
      {complaints.length === 0 && (
        <p className="text-gray-500 text-sm">
          No complaints yet. Click "New Complaint" to submit one.
        </p>
      )}
      <div className="space-y-3">
        {complaints.map((c) => (
          <Link
            to={`/complaint/${c._id}`}
            key={c._id}
            className="block bg-white border rounded-lg p-4 hover:shadow-sm"
          >
            <div className="flex justify-between items-center mb-1">
              <h3 className="font-semibold">{c.title}</h3>
              <Badge text={c.status} type={c.status} />
            </div>
            <p className="text-sm text-gray-600 line-clamp-2">
              {c.description}
            </p>
            <div className="mt-2 flex gap-2 text-xs text-gray-500">
              <Badge text={c.priority} type={c.priority} />
              <span>Category: {c.category}</span>
              <span>
                Created: {new Date(c.createdAt).toLocaleDateString()}
              </span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default ComplaintList;
**/

import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import complaintService from '../../services/complaintService';
import LoadingSpinner from '../common/LoadingSpinner';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  in_progress: 'bg-blue-100 text-blue-800 border-blue-200',
  resolved: 'bg-green-100 text-green-800 border-green-200',
  rejected: 'bg-red-100 text-red-800 border-red-200',
};

const priorityColors = {
  low: 'bg-gray-100 text-gray-700',
  medium: 'bg-orange-100 text-orange-800',
  high: 'bg-red-100 text-red-800',
};

const tabs = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'resolved', label: 'Resolved' },
];

const ComplaintList = () => {
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  const load = async () => {
    try {
      const data = await complaintService.getComplaints();
      setComplaints(data.complaints || []);
    } catch (err) {
      toast.error('Failed to load complaints');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filteredComplaints = useMemo(() => {
    if (activeTab === 'all') return complaints;
    return complaints.filter((c) => c.status === activeTab);
  }, [complaints, activeTab]);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="max-w-6xl mx-auto mt-6 px-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            Your Complaints
          </h2>
          <p className="text-xs text-gray-500 mt-1">
            Track the progress of garbage issues you&apos;ve reported.
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span className="h-2 w-2 rounded-full bg-yellow-400" /> Pending
          <span className="h-2 w-2 rounded-full bg-blue-500" /> In Progress
          <span className="h-2 w-2 rounded-full bg-green-500" /> Resolved
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-3 py-1 rounded-full text-xs sm:text-sm border transition whitespace-nowrap ${
              activeTab === tab.key
                ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
            }`}
          >
            {tab.label}
            {tab.key !== 'all' && (
              <span className="ml-1 text-[10px] text-indigo-100">
                {
                  complaints.filter((c) => c.status === tab.key)
                    .length
                }
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Empty state */}
      {filteredComplaints.length === 0 && (
        <div className="mt-6 bg-white border border-dashed border-gray-300 rounded-xl p-6 text-center">
          <div className="text-4xl mb-2">🧹</div>
          <h3 className="font-semibold text-gray-800 mb-1">
            No complaints in this category
          </h3>
          <p className="text-xs text-gray-500 max-w-sm mx-auto">
            You haven&apos;t submitted any complaints yet, or none match the
            current filter. Click &quot;New Complaint&quot; in the top bar to
            report an issue.
          </p>
        </div>
      )}

      {/* Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
        {filteredComplaints.map((c) => (
          <Link
            to={`/complaint/${c._id}`}
            key={c._id}
            className="group bg-white border rounded-xl p-4 hover:shadow-md hover:border-indigo-200 transition flex flex-col justify-between"
          >
            <div className="flex justify-between items-start gap-2">
              <div className="flex items-start gap-2">
                <div
                  className={`mt-1 h-8 w-8 flex items-center justify-center rounded-lg bg-gradient-to-br ${
                    c.status === 'resolved'
                      ? 'from-green-100 to-emerald-100'
                      : c.status === 'in_progress'
                      ? 'from-blue-100 to-indigo-100'
                      : 'from-yellow-100 to-amber-100'
                  }`}
                >
                  <span className="text-lg">
                    {c.status === 'resolved'
                      ? '✅'
                      : c.status === 'in_progress'
                      ? '⚙️'
                      : '📍'}
                  </span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1 line-clamp-1">
                    {c.title}
                  </h3>
                  <p className="text-xs text-gray-500 mb-1">
                    {c.location?.address || 'No address specified'}
                  </p>
                  <p className="text-xs text-gray-600 line-clamp-2">
                    {c.description}
                  </p>
                </div>
              </div>
              <span
                className={`px-2 py-0.5 rounded-full text-[10px] border ${statusColors[c.status] || 'bg-gray-100 text-gray-700 border-gray-200'}`}
              >
                {c.status.replace('_', ' ')}
              </span>
            </div>

            <div className="mt-3 flex items-center justify-between text-[11px] text-gray-500">
              <div className="flex gap-2 flex-wrap">
                <span
                  className={`px-2 py-0.5 rounded-full ${priorityColors[c.priority] || 'bg-gray-100 text-gray-700'}`}
                >
                  Priority: {c.priority}
                </span>
                <span className="px-2 py-0.5 rounded-full bg-gray-50 border border-gray-100">
                  {c.category}
                </span>
              </div>
              <span>
                {new Date(c.createdAt).toLocaleDateString()}
              </span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default ComplaintList;
